<template>
  <v-form ref="form">
    <v-row class="pa-5" justify="space-around">
      <v-col cols="12">
        <h2 class="primary--text font-weight-bold pt-5 pl-2">
          {{
            edit ? $t('display/advert:edit.title-edit') : $t('display/advert:edit.title-add')
          }}
        </h2>
        <v-card elevation="0" flat tile>
          <v-card-text>
            <v-row justify="space-around">
              <v-col cols="12" md="5">

                <MediaUserContainer
                  v-show="advert.advertType !== 'HTML'"
                  ref="media"
                  :fitScreen="advert.fitScreen"
                  :mediaFile="advert.media"
                  :advertId="advert.id"
                  @newMedia="(v) => { advert.mediaId = v.id; advert.media = v; }"
                  @updateVideoLength="(v) => { advert.mediaLength = v }"
                  @updateType="(v) => { advert.advertType = v }"
                />
              </v-col>
              <v-col cols="12" md="5">
                <h4 class="primary--text font-weight-medium">
                  {{ $t('common:general') }}
                </h4>

                <!-- Name -->
                <v-text-field
                  clearable
                  outlined
                  v-model="advert.name"
                  :label="$t('common:name')"
                  :rules="[ruleRequired($t('utils:rules.required'))]"
                />

                <!-- Playback -->
                <v-text-field
                  clearable
                  outlined
                  v-model="advert.mediaLength"
                  :label="$t('display/advert:edit.labels.playback-duration')"
                  :rules="[ruleRequired($t('utils:rules.required')), ruleNotNegative($t('utils:rules.negative-value'))]"
                />

                <!-- ButtonContent -->
                <v-row
                  v-if="hasRotationOptionEnabled">
                  <v-col cols="11">
                    <v-select
                        :items="buttonContent"
                        item-text="translatedName"
                        item-value="id"
                        :menu-props="{bottom: true, offsetY: true}"
                        v-model="advert.buttonContent"
                        outlined
                        :label="$t('display/advert:edit.labels.button-content')"
                    />
                  </v-col>
                  <v-col cols="1" style="padding-top: 30px">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on">help_outline</v-icon>
                      </template>
                      <span>{{ $t('display/advert:edit.description.button-content') }}</span>
                    </v-tooltip>
                  </v-col>
                </v-row>

                <h4 class="primary--text font-weight-medium">
                  {{ $t('display/advert:edit.titles.brodcast-period') }}
                </h4>
                <!-- Broadcast period -->
                <DateField
                  clearable
                  :disabled="advert.buttonContent == 'BUTTON_CONTENT'"
                  ref="dateStart"
                  :key="dateStartKey"
                  @update="(v) => {advert.dateStart = v; $refs.dateEnd.setMinDate(v)}"
                  :isClearable="true"
                  :label="$t('common:start-date')"
                />

                <DateField
                  clearable
                  :disabled="advert.buttonContent == 'BUTTON_CONTENT'"
                  :key="dateEndKey"
                  ref="dateEnd"
                  @update="(v) => {advert.dateEnd = v; $refs.dateStart.setMaxDate(v)}"
                  :isClearable="true"
                  :label="$t('common:end-date') + $t('common:exclusive')"
                />

                <h4 class="primary--text font-weight-medium">
                  {{ $t('display/advert:edit.titles.brodcast-time') }}
                </h4>

                <!-- Broadcast time -->
                <TimeField
                  :disabled="advert.buttonContent == 'BUTTON_CONTENT'"
                  clearable
                  ref="timeStart"
                  :isClearable="true"
                  @update="(v) => (advert.timeStart = v)"
                  :label="$t('display/advert:edit.labels.time-start')"
                />

                <TimeField
                  :disabled="advert.buttonContent == 'BUTTON_CONTENT'"
                  clearable
                  ref="timeEnd"
                  :isClearable="true"
                  @update="(v) => (advert.timeEnd = v)"
                  :label="$t('display/advert:edit.labels.time-end')"
                />

                <!-- website -->
                <v-text-field
                  v-show="advert.advertType === 'HTML'"
                  outlined
                  v-model="advert.filename"
                  :label="$t('display/advert:edit.labels.website')"
                />

                <v-row>
                  <v-col cols="3" md="3">
                    <!-- Fit screens -->
                    <v-checkbox
                      color="secondary"
                      v-model="advert.fitScreen"
                      :label="$t('display/advert:edit.labels.fit-screen')"
                      :disabled="isFitScreenDisable"
                    />
                  </v-col>

                  <v-col cols="3" md="3">
                    <!-- Checkbox is Active -->
                    <v-checkbox
                      color="secondary"
                      v-model="advert.active"
                      :label="$t('common:active')"
                    />
                  </v-col>

                </v-row>

              </v-col>

            </v-row>
            <AdditionalElevator
                ref="additionalElevator"
                @elevatorIsLoaded="$refs.additionalElevator.elevatorLoaded()"
                @updateElevators="e => elevatorIds = e"
            />
          </v-card-text>
        </v-card>
      </v-col>

    </v-row>
  </v-form>
</template>

<script>

import DateField from '@/modules/core/components/DateField';
import TimeField from '@/modules/core/components/TimeField';
import MediaUserContainer from '@/modules/display/views/advert/form/MediaUserContainer';
import {
  ruleRequired,
  ruleNotNegative
} from '@/modules/core/services/rules';
import EnumUtils from "@/modules/core/services/enum-utils";
import OptionService from "@/modules/settings/services/option-service";
import DateUtils from '@/modules/core/services/date-utils';
import Utils from "@/modules/core/services/utils";
import {i18n} from "@/plugins/i18next";
import AdditionalElevator from "@/modules/display/components/AdditionalElevator";

export default {
  name: 'UserForm',
  components: {
    DateField,
    TimeField,
    MediaUserContainer,
    AdditionalElevator,
  },
  props: {
    edit: Boolean
  },
  data() {
    return {
      advert: {},
      dateEndKey: 0,
      dateStartKey: 1,
      buttonContent: [],
      elevatorIds: [],
      hasRotationOptionEnabled: false,
    }
  },
  computed: {
    isFitScreenDisable() {
      if (!this.advert || !this.advert.media || !this.advert.media.id) {
        return false;
      }

      return this.advert.media.active === 'ACTIVE';
    }
  },
  methods: {
    ruleRequired,
    ruleNotNegative,

    reset() {
      /**
       * There is a little bug in DateField. It uses the v-mask library, and this library is a cause of the
       * problem when popup window is opened, several times without the page reloading.
       * It is set for DateField components the corresponding key, and before the popup window is closed, it is changing the
       * key. It means that Vuejs should umount the "old" component and create the new one.
       */
      this.dateStartKey++;
      this.dateEndKey--;
      this.$refs.form.reset();
      this.$refs.media.reset();
      this.currentTab = null;
      this.$refs.timeEnd.resetData();
      this.$refs.timeStart.resetData();
    },

    initialize(advert, selectAll) {
      this.advert = advert;

      //if id is null -> new advert, have to change some default settings
      if(this.advert.id == null){
        this.advert.fitScreen = true;
        this.advert.active = true;

        this.advert.buttonContent = 'BUTTON_CONTENT';
      }

      //if rotation content is not enabled, we set the buttonContent on, no matter what
      if(!this.hasRotationOptionEnabled){
        this.advert.buttonContent = 'BUTTON_CONTENT';
      }

      let dateStart = DateUtils.parseStringToDate(this.advert.dateStart);
      let dateEnd = DateUtils.parseStringToDate(this.advert.dateEnd);

      if (dateStart > dateEnd) {
        this.advert.dateEnd = this.advert.dateStart;
      }

      if (this.advert.dateStart) {
        this.advert.dateStart = this.$refs.dateStart.parseDTO(this.advert.dateStart);
        this.$refs.dateEnd.setMinDate(this.advert.dateStart);
      }

      if (this.advert.dateEnd) {
        this.advert.dateEnd = this.$refs.dateEnd.parseDTO(this.advert.dateEnd);
        this.$refs.dateStart.setMaxDate(this.advert.dateEnd);
      }

      if (this.advert.timeStart) {
        this.advert.timeStart = this.$refs.timeStart.parseDTO(this.advert.timeStart);
      }

      if (this.advert.timeEnd) {
        this.advert.timeEnd = this.$refs.timeEnd.parseDTO(this.advert.timeEnd);
      }
      if (selectAll !== true)
        this.$refs.additionalElevator.selected = this.advert.specificElevator;
      else
        this.$refs.additionalElevator.selectAll();
    },

    loadButtonContent() {
      EnumUtils.loadEnum('buttonContent', 'display/advert', 'buttonContent',
          (response) => {
            this.buttonContent = response;
          }
      );
    },

    loadRotationOptionCheck(){
      OptionService.hasRotationOption(
          (response) => {
            this.hasRotationOptionEnabled = response.data;
          },
          (error) => {
            Utils.showFailureSnackbar(i18n.i18next.t('snackbar:load-failure', {name: i18n.i18next.t("common:settings")}));
            this.$logger.error("Could not check rotation option, because:" + error);
          }
      )
    },

    validate() {
      return this.$refs.form.validate();
    }
  },
  created() {
    this.loadButtonContent();
    this.loadRotationOptionCheck();
  }
}
</script>
